import { abbreviationFromState } from '@contracts/order/valueNormalizers/State';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import {
  companyAddressFields,
  CompanyMailingAddressDetailsField,
  CompanyMailingAddressField,
  CompanyMailingCityField,
  CompanyMailingStateField,
  CompanyMailingZipCodeField,
  CompanyPhysicalAddressDetailsField,
  CompanyPhysicalAddressField,
  CompanyPhysicalCityField,
  CompanyPhysicalStateField,
  CompanyPhysicalZipCodeField,
} from '@src/permitField/CompanyAddressFields';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = { fields: string[] } & CollapseListener;

export const CompanyAddressSection = (props: Props) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();

  const [
    physicalAddress,
    physicalAddressDetails,
    physicalCity,
    physicalState,
    physicalZipCode,
    mailingAddress,
    mailingAddressDetails,
    mailingCity,
    mailingState,
    mailingZipCode,
  ] = getValues([
    CompanyPhysicalAddressField.fieldName,
    CompanyPhysicalAddressDetailsField.fieldName,
    CompanyPhysicalCityField.fieldName,
    CompanyPhysicalStateField.fieldName,
    CompanyPhysicalZipCodeField.fieldName,
    CompanyMailingAddressField.fieldName,
    CompanyMailingAddressDetailsField.fieldName,
    CompanyMailingCityField.fieldName,
    CompanyMailingStateField.fieldName,
    CompanyMailingZipCodeField.fieldName,
  ]);

  const mailingIsSameAsPhysical =
    physicalAddress === mailingAddress &&
    physicalAddressDetails === mailingAddressDetails &&
    physicalCity === mailingCity &&
    physicalState === mailingState &&
    physicalZipCode === mailingZipCode;

  const { sectionInError } = useSectionError(Object.keys(companyAddressFields));

  if (props.fields.length <= 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      title={t('companyAddress.title')}
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <Stack
          gap={theme.spacing['3']}
          aria-labelledby="physicalAddressSection"
        >
          <Typography
            id="physicalAddressSection"
            component="h4"
            variant="subtitle1"
          >
            {t('companyAddress.physicalSubtitle')}
          </Typography>
          <ConditionalField
            fields={props.fields}
            conditions={CompanyPhysicalAddressField.fieldName}
          >
            <ControllerFormTextInput
              control={control}
              disabled
              fieldName={CompanyPhysicalAddressField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={CompanyPhysicalAddressDetailsField.fieldName}
          >
            <ControllerFormTextInput
              control={control}
              disabled
              fieldName={CompanyPhysicalAddressDetailsField.fieldName}
            />
          </ConditionalField>

          <Stack direction="row" gap={theme.spacing['3']}>
            <ConditionalField
              fields={props.fields}
              conditions={CompanyPhysicalCityField.fieldName}
            >
              <ControllerFormTextInput
                control={control}
                disabled
                fieldName={CompanyPhysicalCityField.fieldName}
              />
            </ConditionalField>
            <ConditionalField
              fields={props.fields}
              conditions={CompanyPhysicalStateField.fieldName}
            >
              <FormDropdown
                disabled
                fieldName={CompanyPhysicalStateField.fieldName}
                options={CompanyPhysicalStateField.options.map((option) => ({
                  value: option,
                  label: abbreviationFromState(option),
                }))}
                control={control}
              />
            </ConditionalField>
            <ConditionalField
              fields={props.fields}
              conditions={CompanyPhysicalZipCodeField.fieldName}
            >
              <ControllerFormTextInput
                control={control}
                disabled
                fieldName={CompanyPhysicalZipCodeField.fieldName}
              />
            </ConditionalField>
          </Stack>
        </Stack>

        {!mailingIsSameAsPhysical && (
          <Stack
            gap={theme.spacing['3']}
            aria-labelledby="mailingAddressSection"
          >
            <Typography
              id="mailingAddressSection"
              component="h4"
              variant="subtitle1"
            >
              {t('companyAddress.mailingSubtitle')}
            </Typography>

            <ConditionalField
              fields={props.fields}
              conditions={CompanyMailingAddressField.fieldName}
            >
              <ControllerFormTextInput
                control={control}
                disabled
                fieldName={CompanyMailingAddressField.fieldName}
              />
            </ConditionalField>
            <ConditionalField
              fields={props.fields}
              conditions={CompanyMailingAddressDetailsField.fieldName}
            >
              <ControllerFormTextInput
                control={control}
                disabled
                fieldName={CompanyMailingAddressDetailsField.fieldName}
              />
            </ConditionalField>

            <Stack direction="row" gap={theme.spacing['3']}>
              <ConditionalField
                fields={props.fields}
                conditions={CompanyMailingCityField.fieldName}
              >
                <ControllerFormTextInput
                  control={control}
                  disabled
                  fieldName={CompanyMailingCityField.fieldName}
                />
              </ConditionalField>
              <ConditionalField
                fields={props.fields}
                conditions={CompanyMailingStateField.fieldName}
              >
                <FormDropdown
                  disabled
                  fieldName={CompanyMailingStateField.fieldName}
                  options={CompanyMailingStateField.options.map((option) => ({
                    value: option,
                    label: abbreviationFromState(option),
                  }))}
                  control={control}
                />
              </ConditionalField>
              <ConditionalField
                fields={props.fields}
                conditions={CompanyMailingZipCodeField.fieldName}
              >
                <ControllerFormTextInput
                  control={control}
                  disabled
                  fieldName={CompanyMailingZipCodeField.fieldName}
                />
              </ConditionalField>
            </Stack>
          </Stack>
        )}
      </Stack>
    </EditOrderCollapsible>
  );
};
